import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { Link } from 'react-router-dom';
import Silk from '../components/Silk';
import { parseISO, isPast, isToday } from 'date-fns';
import avatar from "../pages/avatar.jpg";

export default function Profile() {
  const { user, logout } = useAuth();
  const [hours, setHours] = useState({ theory: 0, practice: 0 });
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theoryBookings, setTheoryBookings] = useState([]);
  
  // Состояние для удаления
  const [deletionStatus, setDeletionStatus] = useState(null);

  // Состояния для поиска учеников (только для инструктора)
  const [searchStudentQuery, setSearchStudentQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Функция склонения стажа
  const getYearWord = (years) => {
    const num = Number(years);
    if (isNaN(num)) return 'лет';
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'лет';
    if (lastDigit === 1) return 'год';
    if (lastDigit >= 2 && lastDigit <= 4) return 'года';
    return 'лет';
  };

  useEffect(() => {
    if (!user?.id) {
      setError('Не авторизован');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Токен отсутствует');

        const [profileRes, hoursRes, deletedRes] = await Promise.all([
          fetch(`/api/profile/${user.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
          fetch('/api/hours'),
          fetch(`/api/user/deleted-status/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!profileRes.ok) {
          const errData = await profileRes.json();
          throw new Error(errData.message || `Ошибка ${profileRes.status}`);
        }

        const data = await profileRes.json();
        const fetchedHours = await hoursRes.json();
        
        if (deletedRes.ok) {
          const deletedData = await deletedRes.json();
          setDeletionStatus(deletedData);
        }

        setProfileData(data);
        setHours(fetchedHours);
        
        if (data.theoryBookings) {
          setTheoryBookings(data.theoryBookings);
        }
      } catch (err) {
        console.error('Ошибка загрузки профиля:', err);
        setError(err.message || 'Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // Обработчик скачивания расписания на сегодня
  const handleDownloadTodaySchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/schedule-doc', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        alert(errorData.message || 'Ошибка при загрузке расписания');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `schedule_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка скачивания:', err);
      alert('Не удалось скачать расписание');
    }
  };

  // Обработчик скачивания расписания на завтра
  const handleDownloadTomorrowSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/schedule-tomorrow', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        alert(errorData.message || 'Ошибка при загрузке расписания на завтра');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      a.download = `schedule_${tomorrowStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка скачивания:', err);
      alert('Не удалось скачать расписание на завтра');
    }
  };

  // Поиск учеников для инструктора
  const handleSearchStudent = async () => {
    if (!searchStudentQuery.trim() || user?.status !== 'mastercar') return;
    
    setSearchLoading(true);
    setSearchError('');
    setSearchResults([]);
    setSelectedStudent(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/instructor/students?search=${encodeURIComponent(searchStudentQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSearchResults(data.students || []);
        if (data.students.length === 0) {
          setSearchError('Ученики не найдены');
        }
      } else {
        setSearchError(data.message || 'Ошибка при поиске');
      }
    } catch (err) {
      console.error('Ошибка поиска:', err);
      setSearchError('Ошибка сервера');
    } finally {
      setSearchLoading(false);
    }
  };

  // Получить детальную информацию об ученике
  const handleSelectStudent = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/instructor/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSelectedStudent(data);
      } else {
        alert(data.message || 'Ошибка загрузки данных ученика');
      }
    } catch (err) {
      console.error('Ошибка загрузки ученика:', err);
      alert('Ошибка сервера');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1b1b1b] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#dedede] text-xl">Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#1b1b1b] flex flex-col items-center justify-center text-red-500 text-xl p-6 text-center">
        <div className="text-6xl mb-6">⚠️</div>
        <div className="mb-6">{error}</div>
        <p className="text-[#b0b0b0] text-base mb-8">Попробуйте перезайти в аккаунт</p>
        <button
          onClick={logout}
          className="px-10 py-5 bg-red-600 text-white font-black rounded-full uppercase tracking-widest text-sm hover:bg-red-700 transition-all shadow-lg shadow-red-900/30"
        >
          Выйти из аккаунта
        </button>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-[#1b1b1b] flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <p className="text-[#b0b0b0] text-xl">Профиль не найден</p>
        </div>
      </div>
    );
  }

  const {
    fullName = 'Не указано',
    phone = 'Не указано',
    email = 'Не указано',
    theoryProgress: rawTheory = 0,
    instructor = { fullName: 'Не назначен', experience: '—', photo: null, phone: '—' },
    photo: studentPhoto = avatar,
    booking = [],
    theoryBookings: dataTheoryBookings = []
  } = profileData;

  const actualTheoryBookings = theoryBookings.length > 0 ? theoryBookings : dataTheoryBookings;
  const theoryCompleted = actualTheoryBookings.length || rawTheory || 0;
  
  // Правильный подсчёт практики: только прошедшие занятия
  const practiceCompleted = booking.filter(b => {
    const bookingDate = parseISO(b.date);
    return isPast(bookingDate) && !isToday(bookingDate);
  }).length;
  
  const theoryProgress = hours.theory > 0 
    ? Math.min(100, Math.round((theoryCompleted / hours.theory) * 100)) 
    : 0;
  const practiceProgress = hours.practice > 0 
    ? Math.min(100, Math.round((practiceCompleted / hours.practice) * 100)) 
    : 0;

  const upcomingBookings = booking.filter(b => {
    const bookingDate = parseISO(b.date);
    return !isPast(bookingDate) || isToday(bookingDate);
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const pastBookings = booking.filter(b => {
    const bookingDate = parseISO(b.date);
    return isPast(bookingDate) && !isToday(bookingDate);
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="min-h-screen relative overflow-hidden bg-[rgba(255, 255, 255, 0.1)] text-[#dedede] font-['Inter',sans-serif] antialiased pb-20 lg:pb-0">
      <Silk speed={0.6} scale={1.2} color="#a49d9d" noiseIntensity={0} rotation={4.25} />

      <div className="relative z-10">
        <Header />

        {/* УВЕДОМЛЕНИЕ ОБ УДАЛЕНИИ */}
        {deletionStatus?.isDeleted && (
          <div className="fixed top-20 left-0 right-0 z-50 flex justify-center px-4">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full border-2 border-red-400">
              <div className="flex items-start gap-4">
                <div className="text-4xl">⚠️</div>
                <div className="flex-1">
                  <h3 className="text-xl font-black uppercase mb-2">Аккаунт будет удалён</h3>
                  <p className="mb-3">
                    Ваш аккаунт помечен для удаления. Осталось дней: <span className="font-bold text-2xl">{deletionStatus.daysLeft}</span>
                  </p>
                  <p className="text-sm opacity-90">
                    Все записи отменены. Для восстановления обратитесь к администратору.
                  </p>
                  <div className="w-full bg-red-900/50 h-2 rounded-full mt-4">
                    <div 
                      className="bg-white h-2 rounded-full transition-all"
                      style={{ 
                        width: `${Math.max(0, 100 - (deletionStatus.daysLeft / 14 * 100))}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <section className="py-20 md:py-24">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12 md:mb-16 text-center">
              <span className="text-xs md:text-[11px] font-black uppercase tracking-[0.4em] md:tracking-[0.5em] text-[var(--primary)] opacity-90">
                Профиль
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter mt-2 md:mt-3">
                Личный кабинет
              </h1>
              <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
                {user?.status === 'student' ? 'Ученик автошколы' : 
                 user?.status === 'mastercar' ? 'Инструктор' : 
                 user?.status === 'staff' ? 'Сотрудник автошколы' : 'Пользователь'}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Левая колонка */}
              <div className="lg:col-span-1 space-y-6">
                {/* Карточка пользователя */}
                <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-xl shadow-black/30">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <img
                        src={
                          user?.status === 'mastercar' 
                            ? profileData?.photoUrl || avatar
                            : studentPhoto || avatar
                        }
                        alt="Фото"
                        className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover mb-6 border-4 border-[#2a2a2a] shadow-lg"
                      />
                      <div className="absolute bottom-6 right-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {user?.status === 'student' ? 'У' : 
                           user?.status === 'mastercar' ? 'И' : 
                           user?.status === 'staff' ? 'С' : '?'}
                        </span>
                      </div>
                    </div>
                    <h2 className="text-2xl md:text-2.5xl font-black uppercase text-center mb-3 tracking-tight">
                      {fullName}
                    </h2>
                    
                    <div className="w-full space-y-4 mt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <span className="text-blue-400">📱</span>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">Телефон</p>
                          <p className="font-medium">{phone}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <span className="text-green-400">✉️</span>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">Email</p>
                          <p className="font-medium break-all">{email}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Инструктор для учеников */}
                {user?.status === 'student' && instructor && instructor.id && (
                  <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-xl shadow-black/25">
                    <h3 className="text-xl font-black uppercase tracking-tight mb-6 text-center">
                      Ваш инструктор
                    </h3>
                    <div className="flex flex-col md:flex-row items-center gap-6">
                      {instructor.photo ? (
                        <img
                          src={instructor.photo}
                          alt="Инструктор"
                          className="w-20 h-20 rounded-full object-cover border-2 border-[#333333] shadow-md"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-[#2a2a2a] flex items-center justify-center text-[#b0b0b0] text-3xl font-bold">
                          👤
                        </div>
                      )}
                      <div className="text-center md:text-left">
                        <div className="text-lg font-bold">{instructor.fullName}</div>
                        <div className="text-[#b0b0b0] mt-1 flex items-center gap-2">
                          <span>🎓</span>
                          <span className="text-sm">
                            Стаж: <span className="text-white font-medium">
                              {instructor.experience || '—'} {getYearWord(instructor.experience)}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[#b0b0b0] mt-1">
                          <span>📱</span>
                          <span className="text-sm">
                            Тел.: <span className="text-white font-medium">{instructor.phone || '—'}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Теоретические занятия */}
                {user?.status === 'student' && theoryBookings.length > 0 && (
                  <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-xl shadow-black/25">
                    <h3 className="text-xl font-black uppercase tracking-tight mb-6 text-center">
                      Теоретические занятия
                    </h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                      {theoryBookings.slice(0, 5).map((item, index) => (
                        <div key={index} className="bg-black/30 p-3 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">{item.date}</span>
                            <span className="text-xs text-slate-500">{item.duration}ч</span>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">{item.topic || `Занятие ${index + 1}`}</p>
                        </div>
                      ))}
                      {theoryBookings.length > 5 && (
                        <p className="text-center text-slate-500 text-sm">
                          и ещё {theoryBookings.length - 5} занятий
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Правая колонка */}
              <div className="lg:col-span-2 space-y-6">
                {/* Прогресс - ТОЛЬКО ДЛЯ СТУДЕНТОВ */}
                {user?.status === 'student' && (
                  <div className="glass-panel rounded-3xl p-6 md:p-8 lg:p-10 shadow-xl shadow-black/30">
                    <h3 className="text-2xl md:text-2.5xl font-black uppercase tracking-tighter mb-8 text-center">
                      Прогресс обучения
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                      {/* Теория */}
                      <div className="flex flex-col items-center">
                        <div className="relative w-40 h-40 md:w-48 md:h-48 lg:w-52 lg:h-52 mb-6">
                          <svg className="w-full h-full" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#334155"
                              strokeWidth="4"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="var(--primary)"
                              strokeWidth="4"
                              strokeDasharray={`${theoryProgress}, 100`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-3xl md:text-4xl font-black text-[var(--primary)]">
                              {theoryProgress}%
                            </div>
                            <div className="text-sm text-slate-400 mt-1">Теория</div>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-medium">Теоретические часы</div>
                          <div className="text-[#b0b0b0] mt-2 text-sm md:text-base">
                            Пройдено: <span className="text-primary font-bold">{theoryCompleted}</span> / {hours.theory} часов
                          </div>
                        </div>
                      </div>

                      {/* Практика */}
                      <div className="flex flex-col items-center">
                        <div className="relative w-40 h-40 md:w-48 md:h-48 lg:w-52 lg:h-52 mb-6">
                          <svg className="w-full h-full" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#334155"
                              strokeWidth="4"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="var(--gold-premium)"
                              strokeWidth="4"
                              strokeDasharray={`${practiceProgress}, 100`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-3xl md:text-4xl font-black text-[var(--gold-premium)]">
                              {practiceProgress}%
                            </div>
                            <div className="text-sm text-slate-400 mt-1">Практика</div>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-medium">Практические занятия</div>
                          <div className="text-[#b0b0b0] mt-2 text-sm md:text-base">
                            Пройдено: <span className="text-[var(--gold-premium)] font-bold">{practiceCompleted}</span> / {hours.practice} занятий
                          </div>
                          <div className="text-xs text-slate-500 mt-2">
                            {upcomingBookings.length} предстоящих, {pastBookings.length} завершённых
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Предстоящие занятия */}
                {user?.status === 'student' && upcomingBookings.length > 0 && (
                  <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-xl shadow-black/30">
                    <h3 className="text-xl font-black uppercase tracking-tight mb-6">
                      Предстоящие занятия ({upcomingBookings.length})
                    </h3>
                    <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                      {upcomingBookings.map((booking, index) => (
                        <div key={index} className="bg-black/30 p-4 rounded-xl">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <div className="bg-primary/20 text-primary font-bold px-3 py-1 rounded-full text-sm">
                                  {booking.date}
                                </div>
                                <div className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm">
                                  {booking.time}
                                </div>
                              </div>
                              <p className="text-slate-300">
                                Место: <span className="font-medium">{booking.place}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Действия */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {user?.status === 'student' && !deletionStatus?.isDeleted && (
                    <>
                      <Link
                        to="/booking"
                        className="glass-panel rounded-3xl p-6 md:p-8 text-center hover:scale-[1.02] transition-all duration-300 group shadow-lg shadow-black/25"
                      >
                        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <span className="text-primary text-2xl">📅</span>
                        </div>
                        <div className="text-lg font-black uppercase tracking-tight">
                          Записаться на занятие
                        </div>
                        <p className="text-slate-400 text-sm mt-2">
                          Выбрать время для практики
                        </p>
                      </Link>

                      <Link
                        to="/payments"
                        className="glass-panel rounded-3xl p-6 md:p-8 text-center hover:scale-[1.02] transition-all duration-300 group shadow-lg shadow-black/25"
                      >
                        <div className="w-16 h-16 rounded-full bg-[var(--gold-premium)]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <span className="text-[var(--gold-premium)] text-2xl">💰</span>
                        </div>
                        <div className="text-lg font-black uppercase tracking-tight">
                          Платежи и история
                        </div>
                        <p className="text-slate-400 text-sm mt-2">
                          Баланс и история оплат
                        </p>
                      </Link>
                    </>
                  )}

                  {/* Кнопки для инструктора */}
                  {user?.status === 'mastercar' && (
                    <>
                      <button
                        onClick={handleDownloadTodaySchedule}
                        className="glass-panel rounded-3xl p-6 md:p-8 text-center hover:scale-[1.02] transition-all duration-300 group shadow-lg shadow-black/25"
                      >
                        <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <span className="text-blue-400 text-2xl">📅</span>
                        </div>
                        <div className="text-lg font-black uppercase tracking-tight">
                          Расписание на сегодня
                        </div>
                        <p className="text-slate-400 text-sm mt-2">
                          Скачать Excel с сегодняшними занятиями
                        </p>
                      </button>

                      <button
                        onClick={handleDownloadTomorrowSchedule}
                        className="glass-panel rounded-3xl p-6 md:p-8 text-center hover:scale-[1.02] transition-all duration-300 group shadow-lg shadow-black/25"
                      >
                        <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <span className="text-green-400 text-2xl">📆</span>
                        </div>
                        <div className="text-lg font-black uppercase tracking-tight">
                          Расписание на завтра
                        </div>
                        <p className="text-slate-400 text-sm mt-2">
                          Скачать Excel с завтрашними занятиями
                        </p>
                      </button>
                    </>
                  )}
                </div>

                {/* БЛОК ПОИСКА УЧЕНИКОВ ДЛЯ ИНСТРУКТОРА */}
                {user?.status === 'mastercar' && (
                  <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-xl shadow-black/30 mt-6">
                    <h3 className="text-xl font-black uppercase tracking-tight mb-6">
                      Поиск учеников
                    </h3>
                    
                    <div className="flex gap-4 mb-6">
                      <input
                        type="text"
                        value={searchStudentQuery}
                        onChange={(e) => setSearchStudentQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
                        placeholder="Введите ФИО ученика"
                        className="flex-1 px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white focus:border-primary focus:outline-none"
                      />
                      <button
                        onClick={handleSearchStudent}
                        disabled={searchLoading}
                        className="px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/80 transition disabled:opacity-50"
                      >
                        {searchLoading ? 'Поиск...' : 'Найти'}
                      </button>
                    </div>
                    
                    {/* Результаты поиска */}
                    {searchError && (
                      <div className="bg-red-900/30 border border-red-500 rounded-xl p-4 text-red-400 mb-4">
                        {searchError}
                      </div>
                    )}
                    
                    {searchResults.length > 0 && !selectedStudent && (
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                        {searchResults.map((student) => (
                          <div
                            key={student.id}
                            onClick={() => handleSelectStudent(student.id)}
                            className="bg-black/30 p-4 rounded-xl cursor-pointer hover:bg-black/50 transition border border-white/5 hover:border-primary/50"
                          >
                            <div className="font-medium">{student.fullName}</div>
                            <div className="text-sm text-slate-400 mt-1">
                              Группа: {student.group || 'Не указана'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Информация о выбранном ученике */}
                    {selectedStudent && (
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-lg font-bold">Информация об ученике</h4>
                          <button
                            onClick={() => setSelectedStudent(null)}
                            className="text-red-400 hover:text-red-300"
                          >
                            ✕ Закрыть
                          </button>
                        </div>
                        
                        <div className="bg-black/30 rounded-xl p-6 space-y-4">
                          {/* Основная информация */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-slate-400">ФИО</p>
                              <p className="font-medium">{selectedStudent.fullName}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-400">Телефон</p>
                              <p className="font-medium">{selectedStudent.phone || '—'}</p>
                            </div>
                          </div>
                          
                          {/* Прогресс */}
                          <div className="border-t border-white/10 pt-4">
                            <h5 className="font-bold mb-3">Прогресс обучения</h5>
                            <div className="space-y-3">
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Практика</span>
                                  <span className="text-[var(--gold-premium)]">
                                    {selectedStudent.practiceProgress || 0} / {selectedStudent.totalPracticeHours || hours.practice} занятий
                                  </span>
                                </div>
                                <div className="w-full bg-gray-700 h-2 rounded-full">
                                  <div
                                    className="bg-[var(--gold-premium)] h-2 rounded-full"
                                    style={{
                                      width: `${Math.min(100, ((selectedStudent.practiceProgress || 0) / (selectedStudent.totalPracticeHours || hours.practice)) * 100)}%`
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Предстоящие занятия */}
                          {selectedStudent.upcomingBookings?.length > 0 && (
                            <div className="border-t border-white/10 pt-4">
                              <h5 className="font-bold mb-3">Предстоящие занятия</h5>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {selectedStudent.upcomingBookings.map((booking, idx) => (
                                  <div key={idx} className="bg-black/20 p-3 rounded-lg text-sm">
                                    <div className="flex justify-between">
                                      <span>{booking.date}</span>
                                      <span className="text-primary">{booking.time}</span>
                                    </div>
                                    <div className="text-slate-400 mt-1">{booking.place}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Выход */}
                <button
                  onClick={logout}
                  className="w-full py-4 md:py-5 bg-gradient-to-r from-red-800 to-red-700 text-white font-black rounded-full uppercase tracking-wider text-lg md:text-xl hover:from-red-900 hover:to-red-800 transition-all shadow-lg shadow-red-900/30"
                >
                  Выйти из аккаунта
                </button>
              </div>
            </div>
          </div>
        </section>

        <BottomNav />
      </div>
    </div>
  );
}